import { multiplyMoney as sharedMultiply } from '@clockwise/shared';

/**
 * Formatting for the money strings the invoice API returns.
 *
 * Every monetary value crosses the wire as a decimal string and stays one
 * through form state — see the money contract in `docs/invoices.md`. This is
 * the ONE place a display path turns one into a number, and it is guarded
 * accordingly.
 */

/**
 * Format a stored amount for display, e.g. `'3528.50'` → `'$3,528.50'`.
 *
 * Returns `null` for anything it cannot honestly render — absent, blank, or
 * non-finite. Callers render an explicit "Unknown" for null rather than a
 * placeholder that looks like a real figure.
 *
 * The blank-string guard is the important one: `Number('')` is `0`, so
 * without it a missing amount would format as a perfectly plausible `$0.00`
 * and nobody would ever know the data was gone (G15).
 */
export function formatMoney(value: string | null | undefined, currency = 'USD'): string | null {
  if (value === null || value === undefined || value.trim() === '') return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(n);
  } catch {
    // An unknown ISO code makes `Intl.NumberFormat` throw. Fall back to the
    // code plus a plain two-decimal figure rather than dropping the row —
    // the number is still the useful part, and swallowing to `null` here
    // would report a data gap that does not exist.
    return `${currency} ${n.toFixed(2)}`;
  }
}

/** A discount is a single signed figure. Quantity × rate is meaningless for
 *  one, and offering the pair invites "-1 × 318.25" — the same number reached
 *  by a route nobody reading the invoice can audit. */
export function hasQuantityAndRate(kind: string): boolean {
  return kind !== 'discount';
}

/**
 * `quantity × rate` as a two-decimal string — or `null` when either side is
 * blank or unparseable, which is the normal state of a half-typed row.
 *
 * Delegates to the shared decimal multiply — the same function the server's
 * validator uses to derive the stored amount, so the figure the user watches
 * appear and the figure that gets saved are produced by one implementation.
 * The regex guards stay: they are the client-side mirror of the server's input
 * rules, so a bad figure surfaces inline rather than as a 400 (G12).
 *
 * Returning `null` rather than `'0.00'` for unusable input matters: a zero
 * would silently overwrite an amount the user had typed by hand (G15).
 */
export function derivedAmount(quantity: string, rate: string): string | null {
  const q = quantity.trim();
  const r = rate.trim();
  if (!/^\d+(\.\d{1,2})?$/.test(q) || !/^-?\d+(\.\d{1,2})?$/.test(r)) return null;
  return sharedMultiply(q, r);
}
